import { BOARD_W, BOARD_H, WORLD_W, WORLD_H } from './engine/constants';
import { PIECES, SHAPES, COLORS } from './engine/tetrominoes';

const STYLE_BLOCK = 0;
const STYLE_GHOST = 1;
const STYLE_PANEL = 2;
const STYLE_LINE = 3;

const FLOATS_PER_INSTANCE = 12;
const STRIDE = FLOATS_PER_INSTANCE * 4;

const VERT = `#version 300 es
layout(location = 0) in vec2 a_corner;
layout(location = 1) in vec2 a_center;
layout(location = 2) in vec2 a_half;
layout(location = 3) in vec4 a_color;
layout(location = 4) in vec4 a_extra; // radius, style, glow, gradient
uniform mat4 u_proj;
out vec2 v_p;
out vec2 v_half;
out vec4 v_color;
out vec4 v_extra;
void main() {
  vec2 world = a_center + a_corner * a_half;
  gl_Position = u_proj * vec4(world, 0.0, 1.0);
  v_p = a_corner * a_half;
  v_half = a_half;
  v_color = a_color;
  v_extra = a_extra;
}`;

const FRAG = `#version 300 es
precision highp float;
in vec2 v_p;
in vec2 v_half;
in vec4 v_color;
in vec4 v_extra;
out vec4 outColor;

float sdRoundBox(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

void main() {
  float r = min(v_extra.x, min(v_half.x, v_half.y));
  float d = sdRoundBox(v_p, v_half, r);
  float aa = max(fwidth(d), 0.0001);
  float inside = 1.0 - smoothstep(-aa, aa, d);
  float style = v_extra.y;
  vec3 col;
  float alpha;

  if (style > 2.5) {
    // grid line
    col = v_color.rgb;
    alpha = v_color.a * inside;
  } else if (style > 1.5) {
    // flat panel
    col = v_color.rgb;
    alpha = v_color.a * inside;
  } else if (style > 0.5) {
    // ghost outline
    float th = max(1.5, min(v_half.x, v_half.y) * 0.22);
    float core = 1.0 - smoothstep(-aa, aa, d + th);
    float ring = clamp(inside - core, 0.0, 1.0);
    col = v_color.rgb;
    alpha = ring * v_color.a + inside * 0.06 * v_color.a;
  } else {
    // solid block: vertical gradient + top-left specular + rim + halo
    float g = clamp(v_p.y / v_half.y * 0.5 + 0.5, 0.0, 1.0);
    col = v_color.rgb * mix(0.66, 1.18, g);
    vec2 hsl = vec2(-v_half.x, v_half.y);
    float spec = smoothstep(1.0, 0.0, distance(v_p, hsl * 0.45) / max(v_half.x, v_half.y));
    col += vec3(0.30) * spec * spec;
    float rim = smoothstep(-aa - max(1.0, min(v_half.x, v_half.y) * 0.12), -aa, d);
    col *= mix(1.0, 0.80, rim);
    alpha = v_color.a * inside;
    float halo = (1.0 - inside) * exp(-max(d, 0.0) / max(1.0, v_half.x * 0.9));
    col += v_color.rgb * v_extra.z * halo * 1.5;
    alpha += halo * v_extra.z * 0.55 * v_color.a;
  }

  if (alpha <= 0.001) discard;
  outColor = vec4(col, alpha);
}`;

const BG_VERT = `#version 300 es
layout(location = 0) in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const BG_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform float u_time;
uniform vec2 u_res;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

void main() {
  vec2 c = v_uv - 0.5;
  c.x *= u_res.x / u_res.y;
  float r = length(c);

  vec3 top = vec3(0.10, 0.03, 0.15);
  vec3 bot = vec3(0.03, 0.01, 0.06);
  vec3 col = mix(bot, top, v_uv.y);

  col += vec3(0.34, 0.05, 0.27) * exp(-r * 2.3);
  col += vec3(0.10, 0.02, 0.20) * exp(-length(c - vec2(0.35, 0.30)) * 4.0);

  // drifting starfield
  vec2 sp = v_uv * vec2(u_res.x / u_res.y, 1.0) * 46.0;
  sp.y += u_time * 0.04;
  vec2 id = floor(sp);
  vec2 gv = fract(sp) - 0.5;
  float h = hash(id);
  float star = smoothstep(0.055, 0.0, length(gv)) * step(0.93, h);
  col += vec3(1.0, 0.72, 0.92) * star * (0.45 + 0.55 * sin(u_time * 2.0 + h * 12.0));

  col *= 1.0 - smoothstep(0.45, 1.15, r) * 0.55;
  outColor = vec4(col, 1.0);
}`;

class Batch {
  constructor(capacity) {
    this.data = new Float32Array(capacity * FLOATS_PER_INSTANCE);
    this.count = 0;
  }

  reset() {
    this.count = 0;
  }

  ensure() {
    if ((this.count + 1) * FLOATS_PER_INSTANCE <= this.data.length) return;
    const next = new Float32Array(this.data.length * 2);
    next.set(this.data);
    this.data = next;
  }

  add(cx, cy, hx, hy, color, alpha, radius, style, glow, gradient) {
    this.ensure();
    const o = this.count * FLOATS_PER_INSTANCE;
    const d = this.data;
    d[o] = cx;
    d[o + 1] = cy;
    d[o + 2] = hx;
    d[o + 3] = hy;
    d[o + 4] = color[0];
    d[o + 5] = color[1];
    d[o + 6] = color[2];
    d[o + 7] = alpha;
    d[o + 8] = radius;
    d[o + 9] = style;
    d[o + 10] = glow;
    d[o + 11] = gradient;
    this.count += 1;
  }
}

const compile = (gl, type, src) => {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(`Shader compile failed: ${gl.getShaderInfoLog(shader)}`);
  }
  return shader;
};

const link = (gl, vsSrc, fsSrc) => {
  const program = gl.createProgram();
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, vsSrc));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, fsSrc));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`Program link failed: ${gl.getProgramInfoLog(program)}`);
  }
  return program;
};

export class Renderer {
  constructor(canvas) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: true,
      premultipliedAlpha: false,
      powerPreference: 'high-performance',
    });
    if (!gl) throw new Error('WebGL2 is not supported in this browser.');
    this.gl = gl;
    this.canvas = canvas;
    this.cssW = 1;
    this.cssH = 1;

    this.normal = new Batch(1024);
    this.additive = new Batch(1024);
    this.proj = new Float32Array(16);

    this.buildQuadProgram(gl);
    this.buildBackgroundProgram(gl);
  }

  buildQuadProgram(gl) {
    this.program = link(gl, VERT, FRAG);
    this.projLoc = gl.getUniformLocation(this.program, 'u_proj');

    this.quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, -1, 1, 1, -1, 1]),
      gl.STATIC_DRAW,
    );

    this.instanceBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.normal.data.byteLength, gl.DYNAMIC_DRAW);

    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, STRIDE, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, STRIDE, 8);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, STRIDE, 16);
    gl.vertexAttribDivisor(3, 1);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.FLOAT, false, STRIDE, 32);
    gl.vertexAttribDivisor(4, 1);

    gl.bindVertexArray(null);
  }

  buildBackgroundProgram(gl) {
    this.bgProgram = link(gl, BG_VERT, BG_FRAG);
    this.bgTimeLoc = gl.getUniformLocation(this.bgProgram, 'u_time');
    this.bgResLoc = gl.getUniformLocation(this.bgProgram, 'u_res');

    this.bgBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bgBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    this.bgVao = gl.createVertexArray();
    gl.bindVertexArray(this.bgVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.bgBuffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  resize(cssW, cssH, dpr) {
    const w = Math.max(1, Math.round(cssW * dpr));
    const h = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      this.canvas.style.width = `${cssW}px`;
      this.canvas.style.height = `${cssH}px`;
    }
    this.cssW = cssW;
    this.cssH = cssH;
  }

  // --- geometry helpers ----------------------------------------------------

  addCell(batch, x, y, size, color, style, glow, alpha = 1) {
    const inset = style === STYLE_BLOCK ? size * 0.045 : style === STYLE_GHOST ? size * 0.09 : 0;
    const s = size - inset * 2;
    const radius = style === STYLE_BLOCK ? s * 0.24 : 0;
    batch.add(x + size / 2, y + size / 2, s / 2, s / 2, color, alpha, radius, style, glow, 1);
  }

  addRoundRect(batch, x, y, w, h, color, alpha, radius, style, glow) {
    batch.add(x + w / 2, y + h / 2, w / 2, h / 2, color, alpha, radius, style, glow, 0);
  }

  addPiece(batch, rect, type, style, glow) {
    const cells = SHAPES[type][0];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    cells.forEach((c) => {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    });
    const bw = maxX - minX + 1;
    const bh = maxY - minY + 1;
    const cell = Math.min((rect.w * 0.72) / bw, (rect.h * 0.66) / bh);
    const startX = rect.x + (rect.w - bw * cell) / 2 - minX * cell;
    const startY = rect.y + (rect.h - bh * cell) / 2 - minY * cell;
    const color = COLORS[type];
    cells.forEach((c) => this.addCell(batch, startX + c.x * cell, startY + c.y * cell, cell, color, style, glow));
  }

  // --- frame ---------------------------------------------------------------

  draw(game, layout, time) {
    const gl = this.gl;
    const { scale } = layout;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);

    // Background.
    gl.disable(gl.BLEND);
    gl.useProgram(this.bgProgram);
    gl.bindVertexArray(this.bgVao);
    gl.uniform1f(this.bgTimeLoc, time);
    gl.uniform2f(this.bgResLoc, this.cssW, this.cssH);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Screen shake folded into the projection.
    const sx = (Math.random() - 0.5) * game.shake;
    const sy = (Math.random() - 0.5) * game.shake;
    const W = this.cssW;
    const H = this.cssH;
    const m = this.proj;
    m[0] = 2 / W; m[1] = 0; m[2] = 0; m[3] = 0;
    m[4] = 0; m[5] = -2 / H; m[6] = 0; m[7] = 0;
    m[8] = 0; m[9] = 0; m[10] = 1; m[11] = 0;
    m[12] = -1 + (2 * sx) / W; m[13] = 1 - (2 * sy) / H; m[14] = 0; m[15] = 1;

    const N = this.normal;
    const A = this.additive;
    N.reset();
    A.reset();

    this.buildScene(N, A, game, layout, scale);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.flush(this.program, this.vao, N);

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    this.flush(this.program, this.vao, A);

    gl.bindVertexArray(null);
  }

  flush(program, vao, batch) {
    if (batch.count === 0) return;
    const gl = this.gl;
    gl.useProgram(program);
    gl.uniformMatrix4fv(this.projLoc, false, this.proj);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    const bytes = batch.count * FLOATS_PER_INSTANCE;
    if (this.instanceBuffer.capacity === undefined || this.instanceBuffer.capacity < bytes * 4) {
      gl.bufferData(gl.ARRAY_BUFFER, batch.data.byteLength, gl.DYNAMIC_DRAW);
      this.instanceBuffer.capacity = batch.data.byteLength;
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, batch.data.subarray(0, bytes));
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, batch.count);
  }

  buildScene(N, A, game, layout, scale) {
    const { board } = layout;
    const cell = scale;

    // Board frame + field.
    this.addRoundRect(N, board.x - cell * 0.22, board.y - cell * 0.22, board.w + cell * 0.44, board.h + cell * 0.44, [0.98, 0.35, 0.75], 0.9, cell * 0.55, STYLE_PANEL, 0.35);
    this.addRoundRect(N, board.x, board.y, board.w, board.h, [0.05, 0.02, 0.09], 0.92, cell * 0.4, STYLE_PANEL, 0);

    // Grid.
    const lineW = Math.max(1, cell * 0.025);
    for (let i = 1; i < BOARD_W; i += 1) {
      const x = board.x + i * cell - lineW / 2;
      this.addRoundRect(N, x, board.y, lineW, board.h, [1, 0.7, 0.9], 0.06, 0, STYLE_LINE, 0);
    }
    for (let j = 1; j < BOARD_H; j += 1) {
      const y = board.y + j * cell - lineW / 2;
      this.addRoundRect(N, board.x, y, board.w, lineW, [1, 0.7, 0.9], 0.06, 0, STYLE_LINE, 0);
    }

    // Locked blocks.
    for (let row = 0; row < BOARD_H; row += 1) {
      for (let col = 0; col < BOARD_W; col += 1) {
        const id = game.board[row * BOARD_W + col];
        if (!id) continue;
        const type = PIECES[id - 1];
        this.addCell(N, board.x + col * cell, board.y + row * cell, cell, COLORS[type], STYLE_BLOCK, 0.12);
      }
    }

    // Ghost + active piece.
    if (game.active) {
      const { type, rot, x, y } = game.active;
      const cells = game.cells(type, rot);
      const color = COLORS[type];
      const gy = game.getGhostY();
      if (gy !== y) {
        cells.forEach((c) => {
          const cx = x + c.x;
          const cy = gy + c.y;
          if (cy < 0) return;
          this.addCell(N, board.x + cx * cell, board.y + cy * cell, cell, color, STYLE_GHOST, 0, 0.6);
        });
      }
      cells.forEach((c) => {
        const cy = y + c.y;
        if (cy < 0) return;
        this.addCell(N, board.x + (x + c.x) * cell, board.y + cy * cell, cell, color, STYLE_BLOCK, 1.0);
      });
    }

    // Side panels.
    this.addRoundRect(N, layout.hold.x, layout.hold.y, layout.hold.w, layout.hold.h, [0.09, 0.03, 0.15], 0.72, cell * 0.32, STYLE_PANEL, 0.1);
    this.addRoundRect(N, layout.score.x, layout.score.y, layout.score.w, layout.score.h, [0.09, 0.03, 0.15], 0.5, cell * 0.32, STYLE_PANEL, 0.05);
    layout.next.forEach((r) => {
      this.addRoundRect(N, r.x, r.y, r.w, r.h, [0.09, 0.03, 0.15], 0.6, cell * 0.32, STYLE_PANEL, 0.08);
    });

    if (game.holdType) this.addPiece(N, layout.hold, game.holdType, STYLE_BLOCK, 0.15);
    game.queue.slice(0, 3).forEach((type, i) => {
      if (layout.next[i]) this.addPiece(N, layout.next[i], type, STYLE_BLOCK, 0.15);
    });

    // Effects (additive).
    game.flashes.forEach((f) => {
      const p = f.t / f.max;
      const y = board.y + f.row * cell;
      this.addRoundRect(A, board.x, y, board.w, cell, [1, 0.85, 0.95], p * 0.75, cell * 0.2, STYLE_PANEL, 0);
    });

    game.particles.forEach((pt) => {
      const pos = layout.worldToPx(pt.x, pt.y);
      const size = pt.size * scale * 2.4;
      const a = Math.max(0, pt.life / pt.max);
      this.addRoundRect(A, pos.x - size / 2, pos.y - size / 2, size, size, pt.color, a * 0.9, size * 0.3, STYLE_PANEL, 0);
    });

    // Subtle glow pulse behind the active piece.
    if (game.active) {
      const { type, x, y, rot } = game.active;
      const cells = game.cells(type, rot);
      const pulse = 0.18 + 0.08 * Math.sin(game.time / 140);
      cells.forEach((c) => {
        const cy = y + c.y;
        if (cy < 0) return;
        this.addRoundRect(A, board.x + (x + c.x) * cell, board.y + cy * cell, cell, cell, COLORS[type], pulse, cell * 0.4, STYLE_PANEL, 0);
      });
    }
  }
}
